import { useState } from "react";
import {
  Shelf,
  Icon,
  Text,
  Button,
  Popover,
  Card,
  Stack,
} from "@chaser/components";
import { toCurrency } from "../../../../util";
import styles from "../AddonOption.module.scss";

const BulkPricingPopover = ({
  open,
  onToggle,
  tierContext,
  onSelectTier,
  currency,
}) => {
  const {
    maxBulkDiscount,
    uniqueTiers,
    minQuantity,
    maxQuantity,
    maxTierPrice,
    resolvedQuantity,
  } = tierContext;
  const [hoveredTierKey, setHoveredTierKey] = useState(null);

  return (
    <Shelf justifyContent="center" alignItems="center">
      <Popover
        shown={open}
        onToggle={onToggle}
        target={(shown) => (
          <Button
            appearance="secondary"
            size="small"
            active={shown}
            className={styles.bulkButton}
            data-trackingid="addon-bulk-pricing-info"
            style={{
              boxShadow: shown ? "0 0 0 3px rgba(59,130,246,0.12)" : "none",
            }}
          >
            <Shelf gap="xsmall" alignItems="center" flexWrap="nowrap">
              <Icon name="tag" size={14} color="Blue" />
              <Text fontSize="small" fontWeight="medium" color="Dark Neutral 1">
                Bulk discounts
              </Text>
              {maxBulkDiscount > 0 && (
                <Text fontSize="xsmall" color="Green" fontWeight="medium">
                  Save up to {maxBulkDiscount}%
                </Text>
              )}
              <Icon
                name={shown ? "arrow-up" : "arrow-down"}
                size={12}
                color="Mid Neutral 1"
              />
            </Shelf>
          </Button>
        )}
      >
        <Card maxWidth="340px" padding="12px">
          <Stack gap="xsmall">
            {uniqueTiers
              .filter((tier) => (tier.endingUnit || maxQuantity) >= minQuantity)
              .map((tier, index, visibleTiers) => {
                const discount =
                  maxTierPrice > 0 && tier.price < maxTierPrice
                    ? Math.round(
                        ((maxTierPrice - tier.price) / maxTierPrice) * 100,
                      )
                    : 0;
                const isLastTier = index === visibleTiers.length - 1;
                const isBestValue = isLastTier && discount > 0;
                const isCurrentTier = tier.endingUnit
                  ? resolvedQuantity >= tier.startingUnit &&
                    resolvedQuantity <= tier.endingUnit
                  : resolvedQuantity >= tier.startingUnit;
                const tierKey = `${tier.price}-${tier.startingUnit}`;
                const isHovered = hoveredTierKey === tierKey;
                const tierEndingUnit = tier.endingUnit || maxQuantity;
                const isDisabled = tierEndingUnit < minQuantity;

                return (
                  <Stack
                    key={tierKey}
                    gap="xxsmall"
                    mb={0}
                    justifyContent="center"
                    className={styles.tierItem}
                    onClick={() => {
                      if (isDisabled) return;
                      onSelectTier(tier.startingUnit);
                      onToggle(false);
                    }}
                    onMouseEnter={() =>
                      !isDisabled && setHoveredTierKey(tierKey)
                    }
                    onMouseLeave={() => setHoveredTierKey(null)}
                    style={{
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.5 : 1,
                      ...(isDisabled
                        ? {
                            backgroundColor: "#f3f4f6",
                          }
                        : isBestValue
                          ? {
                              backgroundColor: isHovered
                                ? "#e0f2fe"
                                : "#f0f9ff",
                              border: "1px solid #bfdbfe",
                            }
                          : isCurrentTier
                            ? {
                                backgroundColor: isHovered
                                  ? "#e5e7eb"
                                  : "#f3f4f6",
                                border: "1px solid #d1d5db",
                              }
                            : {
                                backgroundColor: isHovered
                                  ? "#f3f4f6"
                                  : "transparent",
                              }),
                    }}
                  >
                    <Shelf justifyContent="space-between" alignItems="center">
                      <Text
                        fontSize="small"
                        color="Mid Neutral 1"
                        textAlign="center"
                      >
                        {tier.startingUnit}
                        {tier.endingUnit ? ` - ${tier.endingUnit}` : "+"} units
                      </Text>
                      <Text
                        fontSize="small"
                        fontWeight="bold"
                        color={isBestValue ? "Blue" : "Dark Neutral 1"}
                        textAlign="center"
                      >
                        {tier.price === 0
                          ? "FREE"
                          : toCurrency(tier.price / 100, currency)}
                      </Text>
                    </Shelf>
                    {discount > 0 || isBestValue ? (
                      <Shelf justifyContent="space-between" alignItems="center">
                        {discount > 0 ? (
                          <Shelf gap="xxsmall" alignItems="center">
                            <Icon name="tick" size={12} color="Green" />
                            <Text
                              fontSize="xsmall"
                              color="Green"
                              fontWeight="medium"
                              textAlign="center"
                            >
                              Saving {discount}%
                            </Text>
                          </Shelf>
                        ) : (
                          <div />
                        )}
                        {isBestValue && (
                          <Text
                            fontSize="xsmall"
                            color="Blue"
                            fontWeight="medium"
                            textAlign="center"
                          >
                            Best value
                          </Text>
                        )}
                      </Shelf>
                    ) : null}
                  </Stack>
                );
              })}
          </Stack>
        </Card>
      </Popover>
    </Shelf>
  );
};

export default BulkPricingPopover;
